'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { type LogsInitConfiguration } from '@datadog/browser-logs';
import { blinqDatadogRum } from './rum';

import {
  DEPLOYMENT_DOMAIN,
  DeploymentEnvironment,
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  NEXT_PUBLIC_DATADOG_SITE,
  NEXT_PUBLIC_RELEASE_VERSION,
} from '@env';

type MonitoredEnvironment = 'preview' | 'production';

const sharedLogsInitConfig: LogsInitConfiguration = {
  clientToken: NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: NEXT_PUBLIC_DATADOG_SITE,
  service: NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  version: NEXT_PUBLIC_RELEASE_VERSION,
  forwardErrorsToLogs: true,
  forwardConsoleLogs: 'all',
  forwardReports: 'all',
};

const logsInitConfigs: { [key in MonitoredEnvironment]: LogsInitConfiguration } = {
  production: {
    ...sharedLogsInitConfig,
    sessionSampleRate: 100,
    env: 'production',
  },
  preview: {
    ...sharedLogsInitConfig,
    sessionSampleRate: 100,
    env: 'non-production',
  },
};

// Function to initialize Datadog Logs
const initializeDatadogLogs = async () => {
  if (typeof window !== 'undefined') {
    const isNonProduction = [DeploymentEnvironment.Preview, DeploymentEnvironment.NonProduction].includes(
      DEPLOYMENT_DOMAIN
    );
    const isProduction = DEPLOYMENT_DOMAIN === DeploymentEnvironment.Production;

    if (isProduction || isNonProduction) {
      const { datadogLogs } = await import('@datadog/browser-logs');
      const logsInitConfig = isNonProduction ? logsInitConfigs['preview'] : logsInitConfigs['production'];
      datadogLogs.init(logsInitConfig);
      return datadogLogs; // Return datadogLogs instance
    }
  }

  return null;
};

// MonitoringProvider component that initializes both RUM and Logs
export const MonitoringProvider: React.FC = () => {
  const pathname = usePathname();
  const initializedRef = useRef({ rum: false, logs: false }); // Track both logs and rum initialization

  useEffect(() => {
    const initializeMonitoring = async (initialPathname: string) => {
      if (!initializedRef.current.logs && typeof window !== 'undefined') {
        await initializeDatadogLogs();
        initializedRef.current.logs = true;
      }

      if (!initializedRef.current.rum && typeof window !== 'undefined') {
        initializedRef.current.rum = true;

        // Only start the view on the initial load
        blinqDatadogRum.startView(initialPathname);
      }
    };

    if (pathname) {
      initializeMonitoring(pathname);

      // Watch for changes in pathname and update the view
      if (initializedRef.current.rum) {
        blinqDatadogRum.startView(pathname);
      }
    }
  }, [pathname]); // Effect runs on router path change

  return null; // This component doesn't render anything
};
